import { BulletPool } from './spawning/BulletPool.js';
import { Skirmisher } from '../entities/Skirmisher.js';
import { ARENA } from '../../config/constants.js';

// How far the player has to actually travel before "move" counts as learned —
// far enough that a nervous twitch doesn't clear it, short enough that it's a
// couple of seconds of ordinary walking.
const MOVE_DISTANCE_GOAL = 220;
// Ring cadence: a fresh ring closes in on the player this often until they
// dash through one. Slow on purpose — this is the one telegraph in the game
// the player is *supposed* to have time to think about.
const RING_INTERVAL = 3.2;
const RING_RADIUS = 78;
const RING_BULLETS = 18;
const RING_SPEED = 34;
// How long the closing message holds before the tutorial hands back.
const OUTRO_DURATION = 2.4;

/**
 * The 30-second first-run tutorial (ARCHIVE.md P4). Three beats, in the order
 * a new player needs them: **move**, **dash through something** (the single
 * mechanic the whole bullet-hell layer is built on, and the one nothing else
 * in the game ever explains), then **kill a target** — which is where they
 * find out weapons fire on their own.
 *
 * Runs as its own Game stage rather than a scripted first wave, because it
 * has to be able to guarantee what's on screen. It reuses the real Player,
 * CombatSystem, and bullet pool, so what it teaches is the actual game and
 * not a mock of it.
 *
 * Deliberately unfailable: the ring's bullets deal no damage. A tutorial that
 * can kill you teaches the wrong lesson on the way out, and a death here
 * would need a whole run-end path of its own.
 */
export class TutorialDirector {
  constructor(eventBus, combat, { player }) {
    this.eventBus = eventBus;
    this.combat = combat;
    this.player = player;
    this.pool = new BulletPool(64);
    this.pool.sourceLabel = 'tutorial';
    this.step = 'move';
    this.isComplete = false;

    this._movedDistance = 0;
    this._lastX = player.x;
    this._lastY = player.y;
    this._ringTimer = 0.8; // brief beat before the first ring, so the prompt lands first
    this._outroTimer = OUTRO_DURATION;
    this._dummy = null;
    // Set the first time a ring bullet washes over the player while they
    // weren't dashing — turns the prompt into a correction rather than
    // repeating the same instruction that just didn't land.
    this.missedDash = false;

    this._onMinionDied = () => {
      if (this.step !== 'fight') return;
      this._removeDummy();
      this._advance('outro');
    };
    eventBus.on('minion:died', this._onMinionDied);
  }

  /** The prompt pair drawn over the arena — headline plus the instruction under it. */
  getPrompt() {
    switch (this.step) {
      case 'move':
        return { title: 'MOVE', body: 'Get used to the arena. You are always faster than you think.' };
      case 'dash':
        return {
          title: 'DASH THROUGH IT',
          body: this.missedDash
            ? 'A dash makes you briefly untouchable — go through the ring, not around it.'
            : 'You cannot outrun a ring. Dash into it: you are invulnerable mid-dash.',
        };
      case 'fight':
        return { title: 'DESTROY THE TARGET', body: 'Your weapons fire on their own. Your only job is where you stand.' };
      default:
        return { title: 'THAT IS THE WHOLE GAME', body: 'Move, dash through what you cannot avoid, and pick your ground.' };
    }
  }

  update(dt) {
    this.pool.update(dt);
    switch (this.step) {
      case 'move': return this._updateMove(dt);
      case 'dash': return this._updateDash(dt);
      case 'fight': return this._updateFight(dt);
      default: return this._updateOutro(dt);
    }
  }

  _updateMove() {
    const { player } = this;
    this._movedDistance += Math.hypot(player.x - this._lastX, player.y - this._lastY);
    this._lastX = player.x;
    this._lastY = player.y;
    if (this._movedDistance >= MOVE_DISTANCE_GOAL) this._advance('dash');
  }

  _updateDash(dt) {
    this._ringTimer -= dt;
    if (this._ringTimer <= 0) {
      this._ringTimer = RING_INTERVAL;
      this._fireRing();
    }
    // Contact check, standing in for SpawningSystem's: a bullet caught mid-dash
    // is the lesson landing, and one caught flat-footed is the nudge.
    let dashedThrough = false;
    this.pool.forEachActive((bullet) => {
      const dist = Math.hypot(bullet.x - this.player.x, bullet.y - this.player.y);
      if (dist >= bullet.radius + this.player.radius) return;
      bullet.active = false;
      if (this.player.isInvincible) dashedThrough = true;
      else this.missedDash = true;
    });
    if (dashedThrough) {
      this.eventBus.emit('tutorial:dash-learned');
      this._advance('fight');
    }
  }

  _fireRing() {
    // Fired from a ring around the player and aimed inward, so it closes
    // whichever way they run — the shape that makes dashing the only answer.
    for (let i = 0; i < RING_BULLETS; i++) {
      const angle = (i / RING_BULLETS) * Math.PI * 2;
      const x = this.player.x + Math.cos(angle) * RING_RADIUS;
      const y = this.player.y + Math.sin(angle) * RING_RADIUS;
      this.pool.fire({
        x,
        y,
        vx: -Math.cos(angle) * RING_SPEED,
        vy: -Math.sin(angle) * RING_SPEED,
        radius: 2.2,
        damage: 0, // unfailable by design — see the class comment
        color: '#7e97ff',
        lifetime: 6,
      });
    }
    this.eventBus.emit('tutorial:ring');
  }

  _updateFight(dt) {
    if (!this._dummy) {
      // A target that neither moves nor bites: the point of this beat is that
      // the player discovers auto-fire, not that they survive anything. The
      // fire interval is a large finite number rather than Infinity because
      // Skirmisher desyncs its first shot by multiplying it (Infinity * 0
      // would be NaN, and a NaN timer never counts down predictably).
      this._dummy = new Skirmisher(this.eventBus, {
        x: ARENA.x + ARENA.width * 0.5,
        y: ARENA.y + ARENA.height * 0.28,
        hp: 60,
        moveSpeed: 0,
        contactDamage: 0,
        shotDamage: 0,
        shotSpeed: 0,
        fireInterval: 1e6,
      });
      this.combat.addMinion(this._dummy);
      return;
    }
    // No MinionWaveSystem in this stage, so the dummy is ticked here — it's
    // what ages its hit-flash so shots visibly connect.
    this._dummy.update(dt, this.player, this.pool, [this._dummy]);
  }

  _removeDummy() {
    if (!this._dummy) return;
    this.combat.removeMinion(this._dummy);
    this._dummy = null;
  }

  _updateOutro(dt) {
    this._outroTimer -= dt;
    if (this._outroTimer > 0 || this.isComplete) return;
    this.isComplete = true;
    this.eventBus.emit('tutorial:complete');
  }

  _advance(step) {
    this.step = step;
    // Never let one beat's leftovers muddy the next prompt.
    this.pool.forEachActive((bullet) => { bullet.active = false; });
    this.eventBus.emit('tutorial:step', { step });
  }

  forEachBullet(fn) {
    this.pool.forEachActive(fn);
  }

  destroy() {
    this.eventBus.off?.('minion:died', this._onMinionDied);
  }
}
